import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { Button } from 'reactstrap';
import { cloneDeep } from 'lodash';
import { getValue } from '@truqc/content-path';
import Icon from '../../common/containers/icon';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import { contentSelector, labelSelector, newPathSelector, valueSelector } from '../selectors/index';
import logValueOnChange from '../commonActions/logValueOnChange';
import { downloadCSV, getAllHeaders, normalizeValue } from './qualityAgentView/qualityAgentHelpers';
import { safeAppHashLink } from '../safeAppHashLink';
const resolveRowsAndCount = (value, content, path) => {
  const fromResolved = resolved => {
    if (Array.isArray(resolved) && resolved.length > 0) {
      return {
        rows: resolved,
        rowCount: resolved.length
      };
    }
    if (resolved && Array.isArray(resolved.rows) && resolved.rows.length > 0) {
      const rows = resolved.rows;
      const rowCount = resolved.rowCount !== undefined ? resolved.rowCount : rows.length;
      return {
        rows,
        rowCount
      };
    }
    return null;
  };
  let resolved = fromResolved(value);
  if (!resolved && content != null && path) {
    resolved = fromResolved(getValue(path, content));
  }
  return resolved;
};
const MAX_CELL_DISPLAY_CHARS = 50;
const formatTableCellText = text => {
  const full = text == null ? '' : String(text);
  if (full.length <= MAX_CELL_DISPLAY_CHARS) {
    return {
      display: full,
      titleAttr: undefined
    };
  }
  return {
    display: "".concat(full.slice(0, MAX_CELL_DISPLAY_CHARS), "\u2026"),
    titleAttr: full
  };
};

/**
 * When a cell is an in-app hash route (`#/…`), render it as a link (validated by safeAppHashLink).
 */
const renderHashLinkCell = (plainText, titleFallback, hashHref, hasRowActions) => {
  if (!hashHref) {
    return plainText;
  }
  return /*#__PURE__*/React.createElement("a", {
    href: hashHref,
    className: "t-table-view-hash-link",
    "aria-label": "View",
    title: titleFallback,
    target: "_blank",
    rel: "noopener noreferrer",
    onClick: hasRowActions ? e => e.stopPropagation() : undefined,
    onKeyDown: hasRowActions ? e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
      }
    } : undefined
  }, "View");
};
const TableView = _ref => {
  let _ref$label = _ref.label,
    label = _ref$label === void 0 ? '' : _ref$label,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? undefined : _ref$value,
    _ref$content = _ref.content,
    content = _ref$content === void 0 ? null : _ref$content,
    path = _ref.path,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? '' : _ref$type,
    _ref$contentPath = _ref.contentPath,
    contentPath = _ref$contentPath === void 0 ? undefined : _ref$contentPath,
    _ref$parentContentPat = _ref.parentContentPath,
    parentContentPath = _ref$parentContentPat === void 0 ? undefined : _ref$parentContentPat,
    _ref$variables = _ref.variables,
    variables = _ref$variables === void 0 ? {} : _ref$variables,
    onChange = _ref.onChange,
    _ref$pendingOperation = _ref.pendingOperation,
    pendingOperation = _ref$pendingOperation === void 0 ? false : _ref$pendingOperation,
    _ref$notificationActi = _ref.notificationActions,
    notificationActions = _ref$notificationActi === void 0 ? null : _ref$notificationActi,
    _ref$showDownloadButt = _ref.showDownloadButton,
    showDownloadButton = _ref$showDownloadButt === void 0 ? true : _ref$showDownloadButt,
    _ref$showRowCount = _ref.showRowCount,
    showRowCount = _ref$showRowCount === void 0 ? false : _ref$showRowCount,
    _ref$maxRows = _ref.maxRows,
    maxRows = _ref$maxRows === void 0 ? null : _ref$maxRows,
    _ref$downloadFileName = _ref.downloadFileName,
    downloadFileName = _ref$downloadFileName === void 0 ? 'table-data' : _ref$downloadFileName,
    _ref$postActions = _ref.postActions,
    postActions = _ref$postActions === void 0 ? [] : _ref$postActions,
    _ref$getRowViewHref = _ref.getRowViewHref,
    getRowViewHref = _ref$getRowViewHref === void 0 ? undefined : _ref$getRowViewHref;
  const resolved = useMemo(() => resolveRowsAndCount(value, content, path), [value, content, path]);
  const postActionsList = useMemo(() => Array.isArray(postActions) ? postActions : [], [postActions]);
  const hasRowActions = postActionsList.length > 0;
  const showViewLink = typeof getRowViewHref === 'function';
  const handleRowClick = useCallback(async (row, event) => {
    if (!hasRowActions) return;
    event.stopPropagation();
    if (pendingOperation) {
      var _notificationActions$;
      notificationActions === null || notificationActions === void 0 || (_notificationActions$ = notificationActions.info) === null || _notificationActions$ === void 0 || _notificationActions$.call(notificationActions, {
        message: 'Waiting for changes to save.'
      }, {
        autoClose: 3000
      });
      return;
    }
    await onChange({
      path,
      value: cloneDeep(row),
      changeModifiers: {
        localChange: false,
        postActions: postActionsList,
        type,
        contentPath,
        parentContentPath,
        path,
        variables
      }
    });
  }, [hasRowActions, pendingOperation, notificationActions, onChange, path, type, contentPath, parentContentPath, variables, postActionsList]);
  if (!resolved) {
    return null;
  }
  const rows = resolved.rows,
    rowCount = resolved.rowCount;
  const headers = getAllHeaders(rows);
  const maxN = maxRows == null ? null : Number(maxRows);
  const displayRows = maxN == null || Number.isNaN(maxN) || maxN < 1 ? rows : rows.slice(0, maxN);
  const captionUsesDisplayCap = maxN != null && !Number.isNaN(maxN) && maxN >= 1 && displayRows.length < rows.length;
  const captionRowCount = captionUsesDisplayCap ? displayRows.length : rowCount;
  const captionRowWord = captionRowCount === 1 ? 'row' : 'rows';
  let caption = '';
  if (showRowCount && label) {
    caption = "".concat(label, " (").concat(captionRowCount, " ").concat(captionRowWord, ")");
  } else if (showRowCount) {
    caption = "(".concat(captionRowCount, " ").concat(captionRowWord, ")");
  } else {
    caption = label || '';
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "mb-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-between align-items-center mb-2"
  }, caption ? /*#__PURE__*/React.createElement(Label, {
    value: caption,
    className: "text-muted"
  }) : /*#__PURE__*/React.createElement("div", null), showDownloadButton && /*#__PURE__*/React.createElement(Button, {
    color: "outline-secondary",
    size: "sm",
    onClick: e => {
      e.stopPropagation();
      downloadCSV(rows, downloadFileName);
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "download",
    style: {
      marginRight: 6
    }
  }), "Download CSV")), /*#__PURE__*/React.createElement("div", {
    className: "table-responsive t-table-view-wrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: "table table-sm table-bordered small mb-0 t-table-view".concat(hasRowActions ? ' t-table-view--row-clickable' : '')
  }, /*#__PURE__*/React.createElement("thead", {
    className: "table-light"
  }, /*#__PURE__*/React.createElement("tr", null, headers.map(key => {
    const headerStr = String(key);
    const _formatTableCellText = formatTableCellText(headerStr),
      headerDisplay = _formatTableCellText.display,
      headerTitle = _formatTableCellText.titleAttr;
    return /*#__PURE__*/React.createElement("th", {
      key: "header-".concat(key),
      title: headerTitle
    }, headerDisplay);
  }), showViewLink ? /*#__PURE__*/React.createElement("th", {
    key: "header-view",
    className: "text-nowrap",
    "aria-label": "View"
  }) : null)), /*#__PURE__*/React.createElement("tbody", null, displayRows.map((row, rowIndex) => {
    const rowKey = row.id != null && row.id !== '' ? "row-".concat(rowIndex, "-id-").concat(String(row.id)) : "row-".concat(rowIndex, "-").concat(JSON.stringify(row).substring(0, 50));
    return /*#__PURE__*/React.createElement("tr", {
      key: rowKey,
      onClick: hasRowActions ? e => handleRowClick(row, e) : undefined
    }, headers.map(cellKey => {
      const rawCell = normalizeValue(row[cellKey]);
      const _formatTableCellText2 = formatTableCellText(rawCell),
        display = _formatTableCellText2.display,
        titleAttr = _formatTableCellText2.titleAttr;
      const trimmed = String(rawCell).trim();
      const hashHref = trimmed.startsWith('#/') ? safeAppHashLink(trimmed) : null;
      const linkTitle = titleAttr || trimmed;
      return /*#__PURE__*/React.createElement("td", {
        key: "cell-".concat(cellKey, "-").concat(rowKey),
        title: hashHref ? undefined : titleAttr
      }, renderHashLinkCell(display, linkTitle, hashHref, hasRowActions));
    }), showViewLink ? /*#__PURE__*/React.createElement("td", {
      key: "cell-view-".concat(rowKey),
      className: "text-nowrap"
    }, (() => {
      const rawHref = getRowViewHref(row);
      const href = safeAppHashLink(rawHref);
      if (!href) {
        return null;
      }
      return /*#__PURE__*/React.createElement("a", {
        href: href,
        className: "t-table-view-row-link",
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: e => e.stopPropagation(),
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }
      }, "View");
    })()) : null);
  })))));
};
TableView.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    rowCount: PropTypes.number
  })]),
  content: PropTypes.shape({}),
  path: PropTypes.string.isRequired,
  type: PropTypes.string,
  contentPath: PropTypes.string,
  parentContentPath: PropTypes.string,
  variables: PropTypes.shape({}),
  onChange: PropTypes.func.isRequired,
  pendingOperation: PropTypes.bool,
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }),
  showDownloadButton: PropTypes.bool,
  showRowCount: PropTypes.bool,
  maxRows: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  downloadFileName: PropTypes.string,
  postActions: PropTypes.arrayOf(PropTypes.shape({})),
  getRowViewHref: PropTypes.func
};
export { TableView as UncomposedTableView };
export default entityComposition(TableView, [], {
  label: labelSelector,
  value: valueSelector,
  content: contentSelector,
  path: newPathSelector,
  onChange: logValueOnChange,
  pendingOperation: state => state.form.pendingOperation
}, ['form', 'notification']);